import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Home from '@/pages/home/Home.tsx';
import LoginPage from '@/pages/LoginPage.tsx';
import DashboardLayout from '@/pages/layout/DashboardLayout.tsx';
import SignUpPage from '@/pages/SignUpPage.tsx';
import ConfirmEmailPage from '@/pages/ConfirmEmailPage.tsx';
import ResetPasswordPage from '@/pages/ResetPasswordPage.tsx';
import SetNewPasswordPage from '@/pages/SetNewPasswordPage.tsx';
import ProfilePage from '@/pages/ProfilePage.tsx';
import PlansPage from '@/pages/PlansPage.tsx';
import OpportunitiesPage from '@/pages/opportunities/OpportunitiesPage.tsx';
import NewOpportunityPage from '@/pages/opportunities/NewOpportunityPage.tsx';
import OpportunityDetailPage from '@/pages/opportunities/OpportunityDetailPage.tsx';
import EditOpportunityPage from '@/pages/opportunities/EditOpportunityPage';
import SuitabilityTestPage from '@/pages/SuitabilityTestPage.tsx';
import Dashboard from '@/pages/dashboard/Dashboard.tsx';

import InvestmentsPage from '@/pages/investments/InvestmentsPage.tsx';
import InvestmentDetailPage from '@/pages/investments/InvestmentDetailPage.tsx';
import CreateInvestmentRoute from '@/pages/investments/CreateInvestmentRoute.tsx';
import CreateCoInvestmentPage from '@/pages/investments/CreateCoInvestmentPage';
import UpdateInvestmentPage from '@/pages/investments/UpdateInvestmentPage.tsx';
import InvestmentChecklistPage from '@/pages/investments/InvestmentChecklistPage.tsx';
import AcceptCoInvestmentPage from '@/pages/investments/AcceptCoInvestmentPage';
import AcceptInvestmentInvitationPage from '@/pages/investments/AcceptInvestmentInvitationPage';
import PortfolioReportPage from '@/pages/investments/PortfolioReportPage';
import PortfolioFiscalReportPage from '@/pages/investments/PortfolioFiscalReportPage';
import theme from '@/theme/theme.ts';
import PsisPage from './pages/PsisPage.tsx';
import ToolsPage from '@/pages/ToolsPage.tsx';
import LegalPage from '@/pages/legal/LegalPage.tsx';
import CookieBanner from '@/components/CookieBanner';
import TrainingPage from '@/pages/training/TrainingPage.tsx';
import PartnersPage from '@/pages/partners/PartnersPage.tsx';
import PartnerFormPage from '@/pages/partners/PartnerFormPage.tsx';
import { persistAttributionContext } from '@/lib/analytics/product-events';
import { TEMPORARY_FREE_BILLING_BETA } from '@/features/billing/temporary-free-billing-beta.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // datos considerados frescos durante 5 minutos
      gcTime: 30 * 60 * 1000, // cache time
      retry: 1, // número de reintentos si falla la petición
      refetchOnWindowFocus: false, // no recargar datos al volver a la pestaña
    },
  },
});

function AppContent() {
  const location = useLocation();

  useEffect(() => {
    persistAttributionContext(location.search, document.referrer, location.pathname);
  }, [location.pathname, location.search]);

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={3500}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route element={<DashboardLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/perfil" element={<ProfilePage />} />

            <Route path="/psis" element={<PsisPage />} />
            <Route path="/inversiones" element={<InvestmentsPage />} />
            <Route path="/dashboard/informe" element={<PortfolioReportPage />} />
            <Route path="/dashboard/fiscal" element={<PortfolioFiscalReportPage />} />
            <Route path="/inversiones/nueva" element={<CreateInvestmentRoute />} />
            <Route path="/inversiones/nueva-coinversion" element={<CreateCoInvestmentPage />} />
            <Route path="/inversiones/coinversion/aceptar" element={<AcceptCoInvestmentPage />} />
            <Route
              path="/inversiones/invitacion/aceptar"
              element={<AcceptInvestmentInvitationPage />}
            />
            <Route path="/inversiones/:investmentId/editar" element={<UpdateInvestmentPage />} />
            <Route path="/inversiones/:id" element={<InvestmentDetailPage />} />
            <Route path="/inversiones/:id/checklist" element={<InvestmentChecklistPage />} />

            <Route path="/oportunidades" element={<OpportunitiesPage />} />
            <Route path="/oportunidades/nuevo" element={<NewOpportunityPage />} />
            <Route path="/oportunidades/:id" element={<OpportunityDetailPage />} />
            <Route path="/oportunidades/:id/editar" element={<EditOpportunityPage />} />
            <Route path="/test-idoneidad" element={<SuitabilityTestPage />} />
            <Route path="/formacion" element={<TrainingPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/partners/apply/:partnerId" element={<PartnerFormPage />} />
            <Route path="/herramientas" element={<ToolsPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/altas" element={<SignUpPage />} />
          <Route path="/recuerda" element={<ResetPasswordPage />} />
          <Route path="/restablecer" element={<SetNewPasswordPage />} />
          <Route path="/confirmar-email" element={<ConfirmEmailPage />} />
          <Route
            path="/planes"
            element={
              TEMPORARY_FREE_BILLING_BETA ? <Navigate to="/dashboard" replace /> : <PlansPage />
            }
          />
          <Route path="/legal" element={<LegalPage />} />
        </Routes>
        <CookieBanner />
      </ThemeProvider>
    </SnackbarProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}
